import { IAPIRoutes } from "../../../../config";

export type IClinicRepository = ReturnType<typeof ClinicRepositoryFactory>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const parseClinicContactInfoResponse = (
  response: unknown
): IClinicContactInfo => {
  const envelope = isRecord(response) ? response : null;
  const unwrapped =
    envelope && isRecord(envelope.data) ? envelope.data : response;
  const payload =
    isRecord(unwrapped) && isRecord(unwrapped.contact_info)
      ? unwrapped.contact_info
      : unwrapped;

  if (
    !isRecord(payload) ||
    (!("efax_number" in payload) && !("phone_number" in payload)) ||
    (payload.efax_number !== null &&
      payload.efax_number !== undefined &&
      typeof payload.efax_number !== "string") ||
    (payload.phone_number !== null &&
      payload.phone_number !== undefined &&
      typeof payload.phone_number !== "string")
  ) {
    const responseMessage = envelope?.message;
    const detail = Array.isArray(responseMessage)
      ? responseMessage.filter((item) => typeof item === "string").join("\n")
      : typeof responseMessage === "string"
      ? responseMessage
      : "Invalid clinic contact information response";
    throw new Error(detail);
  }

  return {
    efax_number: payload.efax_number ?? null,
    phone_number: payload.phone_number ?? null,
  };
};

export const ClinicRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, PATCH } = APIProvider;

  const getPsnSettings: IGetPsnSettingsAPI = async ({ clinic_id }) =>
    GET(APIRoutes.apiPsnSettings.replace("{clinic_id}", String(clinic_id)), {});

  const patchPsnSettings: IPatchPsnSettingsAPI = async ({
    clinic_id,
    ...body
  }) =>
    PATCH(
      APIRoutes.apiPsnSettings.replace("{clinic_id}", String(clinic_id)),
      body
    );
  const { getAuthenticationToken } = AuthService;

  const getClinicID: IGetMyClinicAPI = async () =>
    GET(
      APIRoutes.apiMyClinic,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getClinicFloor: IGetClinicFloorAPI = async (obj) =>
    GET(
      APIRoutes.apiGetClinicFloor.replace("{clinic_id}", String(obj.clinic_id)),
      obj,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const setClinicFloor: ISetClinicFloorAPI = async ({ clinic_id, ...obj }) =>
    POST(
      APIRoutes.apiSetClinicFloor.replace("{clinic_id}", clinic_id?.toString()),
      obj,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const logError: ILogErrorAPI = async (obj) =>
    POST(APIRoutes.apiMixPanelErrorStore, obj, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const updateFacilityInformation: IUpdateFacilityInformationAPI = async (
    obj
  ) =>
    PATCH(
      APIRoutes.apiUpdateFacilityInformation.replace(
        "{clinic_id}",
        obj.clinic_id.toString()
      ),
      obj,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getClinicContactInfo: IGetClinicContactInfoAPI = async ({
    clinic_id,
  }) =>
    parseClinicContactInfoResponse(
      await GET(
        APIRoutes.apiClinicContactInfo.replace(
          "{clinic_id}",
          clinic_id.toString()
        ),
        {}
      )
    );

  const replaceClinicContactInfo: IReplaceClinicContactInfoAPI = async ({
    clinic_id,
    ...contactInfo
  }) =>
    parseClinicContactInfoResponse(
      await POST(
        APIRoutes.apiClinicContactInfo.replace(
          "{clinic_id}",
          clinic_id.toString()
        ),
        contactInfo
      )
    );

  const patchClinicContactInfo: IPatchClinicContactInfoAPI = async ({
    clinic_id,
    ...contactInfo
  }) =>
    parseClinicContactInfoResponse(
      await PATCH(
        APIRoutes.apiClinicContactInfo.replace(
          "{clinic_id}",
          clinic_id.toString()
        ),
        contactInfo
      )
    );

  const getClinicConfig: IGetClinicConfigAPI = async ({ clinic_id }) =>
    GET(
      APIRoutes.apiClinicConfig.replace("{clinic_id}", clinic_id.toString()),
      {}
    );

  const patchClinicConfig: IPatchClinicConfigAPI = async ({
    clinic_id,
    ...obj
  }) =>
    PATCH(
      APIRoutes.apiClinicConfig.replace("{clinic_id}", clinic_id.toString()),
      obj
    );

  const getClinicShiftReportConfig: IGetClinicShiftReportConfigAPI = async ({
    clinic_id,
  }) =>
    GET(
      APIRoutes.apiClinicShiftReportConfig.replace(
        "{clinic_id}",
        clinic_id.toString()
      ),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  return {
    getClinicFloor,
    getClinicID,
    setClinicFloor,
    logError,
    updateFacilityInformation,
    getClinicContactInfo,
    replaceClinicContactInfo,
    patchClinicContactInfo,
    getPsnSettings,
    patchPsnSettings,
    getClinicConfig,
    patchClinicConfig,
    getClinicShiftReportConfig,
  };
};
